<template>
  <div class="statistic">
    <div class="statistic-filter">
      <div class="filters">
        <form class="form" id="statistic-form">
          <div class="form-block form-block--30">
            <DatePicker v-model="requestDTO.fromDate" mode="dateTime" :model-modifiers="{string: true}"  is24hr :masks="{modelValue: 'YYYY-MM-DD HH:mm:ss', inputDateTime24hr: 'YYYY-MM-DD HH:mm:ss'}">
              <template v-slot="{ inputValue, togglePopover, inputEvents }">
                <input
                  class="form-control form-control-sm"
                  :placeholder="helper.trans('labels.from_date')"
                  :value="inputValue"
                  @focus="togglePopover"
                  @change="inputEvents.change"
                />
              </template>
            </DatePicker>
          </div>

          <div class="form-block form-block--30">
            <DatePicker v-model="requestDTO.toDate" mode="dateTime" :model-modifiers="{string: true}"  is24hr :masks="{modelValue: 'YYYY-MM-DD HH:mm:ss', inputDateTime24hr: 'YYYY-MM-DD HH:mm:ss'}">
              <template v-slot="{ inputValue, togglePopover, inputEvents }">
                <input
                  class="form-control form-control-sm"
                  :placeholder="helper.trans('labels.to_date')"
                  :value="inputValue"
                  @focus="togglePopover"
                  @change="inputEvents.change"
                />
              </template>
            </DatePicker>
          </div>

          <div class="form-block form-block--30">
            <Multiselect id="methods"
                         class="multiselect-sm"
                         v-model="requestDTO.methods"
                         :options="store.getters['currencyMethods/methods']()"
                         :custom-label="(opt) => helper.trans(`pay_methods.${opt}`)"
                         :multiple="true"
                         select-label="Select"
                         :placeholder="helper.trans('labels.payment_methods')"
            >
              <template #option="props">
                <div class="pay-method">
                  <PayMethodIcon icon-class="icon" :method="props.option"></PayMethodIcon>
                  <span class="overflow-hidden" style="white-space: nowrap; text-overflow: ellipsis">{{ helper.trans('pay_methods.' + props.option) }}</span>
                </div>
              </template>
            </Multiselect>
          </div>
          <div class="form-block form-block--30">
            <Multiselect id="options"
                         class="multiselect-sm"
                         v-model="requestDTO.options"
                         :options="store.getters['currencyMethods/options']()"
                         :custom-label="(opt) => helper.trans(`payment_options.${opt}`)"
                         :multiple="true"
                         select-label="Select"
                         :placeholder="helper.trans('labels.payment_options')"
            >
            </Multiselect>
          </div>

          <div class="form-block form-block--30">
            <DevicesMultiselect ref="deviceMultiselect" :multiple="true" :trader-ids="tradersFilter.map((item) => item.id)" v-model="devicesFilter">
            </DevicesMultiselect>
          </div>
          <div class="form-block form-block--30" v-if="store.getters.isTrader || store.getters.isTraderOperator">
            <input
              class="form-control form-control-sm"
              type="text"
              v-model="requestDTO.requisites"
              @keydown.enter.prevent="applyFilters"
              :placeholder="helper.trans('labels.requisitesOrHolder')"
            />
          </div>
          <div class="form__controls w-100">
            <LoaderButton :on-click="applyFilters" class="form__btn btn">
              <span class="text-capitalize-first">{{$t('button.apply')}}</span>
            </LoaderButton>
            <LoaderButton :on-click="clearFilters" class="form__btn form__btn--reset btn btn--2">
              <span class="text-capitalize-first">{{$t('button.reset')}}</span>
            </LoaderButton>
            <LoaderButton v-if="canExport" :on-click="downloadReport" class="form__btn btn btn-sm rounded-pill ms-auto">
              <span class="text-capitalize-first">{{$t('button.export')}}</span>
            </LoaderButton>
          </div>
        </form>
      </div>
    </div>
    <LoaderContent ref="loader">
      <template v-for="group in sortedGroups" :key="group.groupId">
        <div class="statistic-block" v-for="(typeStat, type) in group.byDirections" :key="type">
          <div class="statistic-block__title">
            <span class="text-capitalize-first"> {{$t(`direction.${type}`)}}</span>
          </div>

          <template v-for="status in statuses" :key="status">
            <div v-if="typeStat[status]" class="statistic-block__container" :class="{'statistic-block__container--summary': status === 'total'}">
              <div class="statistic-block__top">
                <div class="statistic-block__info">
                  <div class="statistic-block__info-title">
                    <span class="text-capitalize-first">{{$t(`deal_status.${status}`)}}</span>
                  </div>
                  <div class="statistic-block__count">
                    {{ numberFormatter.formatNumber(typeStat[status].count) }}
                    <sup>{{numberFormatter.formatNumber(group.countByDirections[type] ? typeStat[status].count/group.countByDirections[type] * 100 : 0, 1) }}%</sup>
                  </div>
                </div>
              </div>
              <div class="statistic-block__details">
                <div class="statistic-block__detail">
                  <span class="text-capitalize-first">{{ $t('labels.taker_sum') }}</span>
                  <div class="statistic-block__sum" v-for="(sum, currency) in typeStat[status].takerSums" :key="currency">
                    {{ numberFormatter.formatMoney(sum) }}
                  </div>
                </div>
                <div class="statistic-block__detail">
                  <span class="text-capitalize-first">{{ type === Direction.PAY_IN ? $t('labels.trader_sum_out') : $t('labels.trader_sum_in') }}</span>
                  <div class="statistic-block__sum" v-for="(sum, currency) in typeStat[status].makerSums" :key="currency">
                    {{ status !== DealStatus.COMPLETED ? '' : type === Direction.PAY_IN ? '-' : '+' }}{{ numberFormatter.formatMoney(sum, usdtSubunits) }}
                  </div>
                </div>
                <div class="statistic-block__detail">
                  <span class="text-capitalize-first">{{ $t('labels.rewards') }}</span>
                  <div class="statistic-block__sum" v-for="(sum, currency) in typeStat[status].traderRewardSums" :key="currency">
                    {{status !== DealStatus.COMPLETED ? '' : '+'}}{{ numberFormatter.formatMoney(sum, usdtSubunits) }}
                  </div>
                </div>
              </div>
            </div>
          </template>

        </div>
      </template>

    </LoaderContent>
  </div>
</template>
<script lang="ts">
import LoaderWrapper from "@/components/LoaderWrapper.vue";
import {computed, defineComponent, onMounted, reactive, ref, toRefs} from 'vue'
import {dateHelper} from "@/service/DateHelper";
import {numberFormatter} from "@/service/NumberFormatter";
import APIClient from "@/service/APIClient";
import {useStore} from "vuex";
import {$t, debounce, helper} from "@/service/CommonHelper";
import {useNotification} from "@kyvg/vue3-notification";
import CopyButton from "@/components/CopyButton.vue";
import {BPagination} from "bootstrap-vue-3";
import {UserRole} from "@/models/enum/UserRole";
import {DealStatus} from "@/models/enum/DealStatus";
import {Dictionary} from "@/types";
import MoneyDTO from "@/models/MoneyDTO";
import {Direction} from "@/models/enum/Direction";
import PaginationDTO from "@/models/PaginationDTO";
import MerchantStoreDTO from "@/models/MerchantStoreDTO";
import TraderMultiselect from "@/components/TraderMultiselect.vue";
import StoreMultiselect from "@/components/StoreMultiselect.vue";
import TraderDTO from "@/models/TraderDTO";
import {DatePicker} from "v-calendar";
import Multiselect from "vue-multiselect";
import moment from "moment";
import DevicesMultiselect from "@/components/DevicesMultiselect.vue";
import TraderDeviceDTO from "@/models/TraderDeviceDTO";
import TrafficGroupMultiselect from "@/components/TrafficGroupMultiselect.vue";
import TrafficGroupDTO from "@/models/TrafficGroupDTO";
import TeamLeadMultiselect from "@/components/TeamLeadMultiselect.vue";
import UserDTO from "@/models/UserDTO";
import PayMethodIcon from "@/components/PayMethodIcon.vue";
import OrderByDTO from "@/models/OrderByDTO";
import FormInputWrapper from "@/components/FormInputWrapper.vue";
import MerchantMultiselect from "@/components/MerchantMultiselect.vue";
import MerchantDTO from "@/models/MerchantDTO";
import LoaderContent from "@/apps/public/components/LoaderContent.vue";
import LoaderButton from "@/apps/public/components/LoaderButton.vue";

const api = new APIClient();
const notification = useNotification()

interface SummaryStat {
  type: string,
  status: string,
  count: number,
  makerSums: Dictionary<MoneyDTO>,
  takerSums: Dictionary<MoneyDTO>,
  traderRewardSums: Dictionary<MoneyDTO>,
  traderRefRewardSums: Dictionary<MoneyDTO>,
  merchantRefRewardSums: Dictionary<MoneyDTO>,
  storeSums: Dictionary<MoneyDTO>,
  storeFeeSums: Dictionary<MoneyDTO>,
  serviceRewardSums: Dictionary<MoneyDTO>,
  serviceAdditionalRewardSums: Dictionary<MoneyDTO>,
  partnersRewards: Dictionary<Dictionary<MoneyDTO>>
}

interface GroupSummaryStat {
  groupId: string,
  store: MerchantStoreDTO|null,
  trader: TraderDTO|null,
  byDirections: Dictionary<Dictionary<SummaryStat>>,
  countByDirections: Dictionary<number>,
  dealsCountByDirections: Dictionary<number>,
}

const orderByFields = ['taker_sum', 'count']
const statuses = ['total', 'rejected', DealStatus.COMPLETED, DealStatus.CANCELED, DealStatus.DISPUTE, DealStatus.TRANSFER_WAITING, DealStatus.TRANSFER_CONFIRMED, DealStatus.NEW]

export default defineComponent({
  methods: {$t},
  components: {
    LoaderButton,
    MerchantMultiselect,
    FormInputWrapper,
    PayMethodIcon,
    TeamLeadMultiselect,
    TrafficGroupMultiselect,
    DatePicker,
    Multiselect,
    StoreMultiselect,
    TraderMultiselect,
    LoaderContent,
    CopyButton,
    BPagination,
    DevicesMultiselect,
  },
  setup() {
    const store = useStore()
    const loader = ref<InstanceType<typeof LoaderContent> | null>(null)
    const deviceMultiselect = ref<InstanceType<typeof DevicesMultiselect> | null>(null)

    let defaultGroupBy = 'store';
    if (store.getters.isTrader || store.getters.isTraderOperator || store.getters.isTeamLead) {
      defaultGroupBy = 'trader'
    } else if (store.getters.isReferral || store.getters.isMerchant || store.getters.isMerchantOperator) {
      defaultGroupBy = 'store'
    }

    let mainFields = ['traders', 'stores', 'fee'];
    let rewardFields = ['trader', 'teamlead', 'referral', 'service'];
    if (store.getters.isTrader || store.getters.isTraderOperator) {
      mainFields = ['traders'];
      rewardFields = ['trader']
    } else if (store.getters.isTeamLead) {
      mainFields = ['traders'];
      rewardFields = ['trader', 'teamlead']
    } else if (store.getters.isMerchant || store.getters.isMerchantOperator) {
      mainFields = ['traders', 'stores', 'fee'];
      rewardFields = []
    } else if (store.getters.isReferral) {
      mainFields = ['traders', 'stores', 'fee'];
      rewardFields = ['referral']
    }


    const state = reactive({
      usdtSubunits: 2,
      tradersFilter: [] as TraderDTO[],
      merchantsFilter: [] as MerchantDTO[],
      storesFilter: [] as MerchantStoreDTO[],
      trafficGroupsFilter: [] as TrafficGroupDTO[],
      devicesFilter: [] as TraderDeviceDTO[],
      teamLeadFilter: null as UserDTO|null,
      groups: {} as Dictionary<GroupSummaryStat>,
      orderByStatus: 'total' as string,
      orderByType: Direction.PAY_IN as Direction,
      orderBy: {
        field: 'taker_sum',
        direction: 'desc'
      } as OrderByDTO,
      requestDTO: {
        groupBy: defaultGroupBy,
        fromDate: moment().format('YYYY-MM-DD 00:00:00'),
        currency: null,
        amountFrom: null,
        amountTo: null,
        requisites: null,
      } as any,
      partners: [] as any[]
    });


    const onTraderListUpdate = debounce(() => {
      deviceMultiselect.value?.search('')
    }, 1000);


    const loadPartners =  async () => {
      if (store.getters.isAdmin) {
        state.partners = await api.partners()
      }
    }

    const loadSummary = async () => {
      loader.value?.show()
      state.requestDTO.traderIds = state.tradersFilter.map((value) => value.id)
      state.requestDTO.storeIds = state.storesFilter.map((value) => value.id)
      state.requestDTO.merchantIds = state.merchantsFilter.map((value) => value.id)
      state.requestDTO.trafficGroupsIds = state.trafficGroupsFilter.map((value) => value.id)
      state.requestDTO.deviceIds = state.devicesFilter.map((value) => value.id)
      state.requestDTO.teamLeadId = state.teamLeadFilter?.id

      try {
        const stats = await api.statisticSummary({
          ...state.requestDTO,
          fromDate: dateHelper.toUTC(state.requestDTO.fromDate),
          toDate: dateHelper.toUTC(state.requestDTO.toDate),
          amountFrom: state.requestDTO.amountFrom,
          amountTo: state.requestDTO.amountTo,
        });

        const groups = {} as Dictionary<GroupSummaryStat>
        let groupIds = [];

        const totalGroup = {
          store: null,
          trader: null,
          groupId: '0',
          byDirections: {
            in: {},
            out: {},
          },
          countByDirections: {
            in: 0,
            out: 0,
          },
          dealsCountByDirections: {
            in: 0,
            out: 0,
          }
        } as GroupSummaryStat

        for (const item of stats) {

          if (!groups[item.groupId]) {
            groupIds.push(item.groupId)
            groups[item.groupId] = {
              store: null,
              trader: null,
              groupId: item.groupId,
              byDirections: {},
              countByDirections: {},
              dealsCountByDirections: {}
            } as GroupSummaryStat
          }

          if (!groups[item.groupId].byDirections[item.type]) {
            groups[item.groupId].byDirections[item.type] = {}
          }

          if (!groups[item.groupId].countByDirections[item.type]) {
            groups[item.groupId].countByDirections[item.type] = 0
          }

          if (!groups[item.groupId].dealsCountByDirections[item.type]) {
            groups[item.groupId].dealsCountByDirections[item.type] = 0
          }

          if (!groups[item.groupId].byDirections[item.type][item.status]) {
            groups[item.groupId].byDirections[item.type][item.status] = {
              type: item.type,
              status: item.status,
              count: 0,
              makerSums: {},
              takerSums: {},
              traderRewardSums: {},
              traderRefRewardSums: {},
              merchantRefRewardSums: {},
              storeSums: {},
              storeFeeSums: {},
              serviceRewardSums: {},
              serviceAdditionalRewardSums: {},
              partnersRewards: {}
            } as SummaryStat
          }

          if (!groups[item.groupId].byDirections[item.type]['processed']) {
            groups[item.groupId].byDirections[item.type]['processed'] = {
              type: item.type,
              status: 'processed',
              count: 0,
              makerSums: {},
              takerSums: {},
              traderRewardSums: {},
              traderRefRewardSums: {},
              merchantRefRewardSums: {},
              storeSums: {},
              storeFeeSums: {},
              serviceRewardSums: {},
              serviceAdditionalRewardSums: {},
              partnersRewards: {}
            } as SummaryStat
          }

          if (!groups[item.groupId].byDirections[item.type]['total']) {
            groups[item.groupId].byDirections[item.type]['total'] = {
              type: item.type,
              status: 'total',
              count: 0,
              makerSums: {},
              takerSums: {},
              traderRewardSums: {},
              traderRefRewardSums: {},
              merchantRefRewardSums: {},
              storeSums: {},
              storeFeeSums: {},
              serviceRewardSums: {},
              serviceAdditionalRewardSums: {},
              partnersRewards: {}
            } as SummaryStat
          }

          if (!totalGroup.byDirections[item.type][item.status]) {
            totalGroup.byDirections[item.type][item.status] = {
              type: item.type,
              status: item.status,
              count: 0,
              makerSums: {},
              takerSums: {},
              traderRewardSums: {},
              traderRefRewardSums: {},
              merchantRefRewardSums: {},
              storeSums: {},
              storeFeeSums: {},
              serviceRewardSums: {},
              serviceAdditionalRewardSums: {},
              partnersRewards: {}
            } as SummaryStat
          }

          if (!totalGroup.byDirections[item.type]['processed']) {
            totalGroup.byDirections[item.type]['processed'] = {
              type: item.type,
              status: 'processed',
              count: 0,
              makerSums: {},
              takerSums: {},
              traderRewardSums: {},
              traderRefRewardSums: {},
              merchantRefRewardSums: {},
              storeSums: {},
              storeFeeSums: {},
              serviceRewardSums: {},
              serviceAdditionalRewardSums: {},
              partnersRewards: {}
            } as SummaryStat
          }

          if (!totalGroup.byDirections[item.type]['total']) {
            totalGroup.byDirections[item.type]['total'] = {
              type: item.type,
              status: 'total',
              count: 0,
              makerSums: {},
              takerSums: {},
              traderRewardSums: {},
              traderRefRewardSums: {},
              merchantRefRewardSums: {},
              storeSums: {},
              storeFeeSums: {},
              serviceRewardSums: {},
              serviceAdditionalRewardSums: {},
              partnersRewards: {}
            } as SummaryStat
          }

          let stat = groups[item.groupId].byDirections[item.type][item.status]
          let totalStat = totalGroup.byDirections[item.type][item.status]

          let processedStat = groups[item.groupId].byDirections[item.type]['processed']
          let processedTotalStat = totalGroup.byDirections[item.type]['processed']

          let totalStatusStat = groups[item.groupId].byDirections[item.type]['total']
          let totalStatusTotalStat = totalGroup.byDirections[item.type]['total']


          stat.count += item.count
          totalStat.count += item.count
          if (!['rejected', 'completed_with_dispute', 'completed_with_dispute_with_attach', 'completed_with_dispute_without_attach', 'completed_without_dispute'].includes(item.status)) {
            groups[item.groupId].dealsCountByDirections[item.type] = groups[item.groupId].dealsCountByDirections[item.type] + item.count
            totalGroup.dealsCountByDirections[item.type] = totalGroup.dealsCountByDirections[item.type] + item.count
            processedStat.count += item.count
            processedTotalStat.count += item.count
          }

          if (!['completed_with_dispute', 'completed_with_dispute_with_attach', 'completed_with_dispute_without_attach', 'completed_without_dispute'].includes(item.status)) {
            groups[item.groupId].countByDirections[item.type] = groups[item.groupId].countByDirections[item.type] + item.count
            totalGroup.countByDirections[item.type] = totalGroup.countByDirections[item.type] + item.count
            totalStatusStat.count += item.count
            totalStatusTotalStat.count += item.count
          }

          if (item.takerSum) {
            stat.takerSums[item.takerSum.currency] = helper.sum(stat.takerSums[item.takerSum.currency], item.takerSum);
            totalStat.takerSums[item.takerSum.currency] = helper.sum(totalStat.takerSums[item.takerSum.currency], item.takerSum);

            if (!['rejected', 'completed_with_dispute', 'completed_with_dispute_with_attach', 'completed_with_dispute_without_attach', 'completed_without_dispute'].includes(item.status)) {
              processedStat.takerSums[item.takerSum.currency] = helper.sum(processedStat.takerSums[item.takerSum.currency], item.takerSum);
              processedTotalStat.takerSums[item.takerSum.currency] = helper.sum(processedTotalStat.takerSums[item.takerSum.currency], item.takerSum);
            }

            if (!['completed_with_dispute', 'completed_with_dispute_with_attach', 'completed_with_dispute_without_attach', 'completed_without_dispute'].includes(item.status)) {
              totalStatusStat.takerSums[item.takerSum.currency] = helper.sum(totalStatusStat.takerSums[item.takerSum.currency], item.takerSum);
              totalStatusTotalStat.takerSums[item.takerSum.currency] = helper.sum(totalStatusTotalStat.takerSums[item.takerSum.currency], item.takerSum);
            }
          }
          if (item.makerSum) {
            stat.makerSums[item.makerSum.currency] = helper.sum(stat.makerSums[item.makerSum.currency], item.makerSum);
            totalStat.makerSums[item.makerSum.currency] = helper.sum(totalStat.makerSums[item.makerSum.currency], item.makerSum);
          }
          if (item.traderRewardSum) {
            stat.traderRewardSums[item.traderRewardSum.currency] = helper.sum(stat.traderRewardSums[item.traderRewardSum.currency], item.traderRewardSum);
            totalStat.traderRewardSums[item.traderRewardSum.currency] = helper.sum(totalStat.traderRewardSums[item.traderRewardSum.currency], item.traderRewardSum);
          }

          if (item.traderRefRewardSum) {
            stat.traderRefRewardSums[item.traderRefRewardSum.currency] = helper.sum(stat.traderRefRewardSums[item.traderRefRewardSum.currency], item.traderRefRewardSum);
            totalStat.traderRefRewardSums[item.traderRefRewardSum.currency] = helper.sum(totalStat.traderRefRewardSums[item.traderRefRewardSum.currency], item.traderRefRewardSum);
          }

          if (item.merchantRefRewardSum) {
            stat.merchantRefRewardSums[item.merchantRefRewardSum.currency] = helper.sum(stat.merchantRefRewardSums[item.merchantRefRewardSum.currency], item.merchantRefRewardSum);
            totalStat.merchantRefRewardSums[item.merchantRefRewardSum.currency] = helper.sum(totalStat.merchantRefRewardSums[item.merchantRefRewardSum.currency], item.merchantRefRewardSum);
          }

          if (item.storeSum) {
            stat.storeSums[item.storeSum.currency] = helper.sum(stat.storeSums[item.storeSum.currency], item.storeSum);
            totalStat.storeSums[item.storeSum.currency] = helper.sum(totalStat.storeSums[item.storeSum.currency], item.storeSum);
          }

          if (item.storeFeeSum) {
            stat.storeFeeSums[item.storeFeeSum.currency] = helper.sum(stat.storeFeeSums[item.storeFeeSum.currency], item.storeFeeSum);
            totalStat.storeFeeSums[item.storeFeeSum.currency] = helper.sum(totalStat.storeFeeSums[item.storeFeeSum.currency], item.storeFeeSum);
          }

          if (item.serviceRewardSum) {
            stat.serviceRewardSums[item.serviceRewardSum.currency] = helper.sum(stat.serviceRewardSums[item.serviceRewardSum.currency], item.serviceRewardSum);
            totalStat.serviceRewardSums[item.serviceRewardSum.currency] = helper.sum(totalStat.serviceRewardSums[item.serviceRewardSum.currency], item.serviceRewardSum);
          }

          if (item.serviceAdditionalRewardSum) {
            stat.serviceAdditionalRewardSums[item.serviceAdditionalRewardSum.currency] = helper.sum(stat.serviceAdditionalRewardSums[item.serviceAdditionalRewardSum.currency], item.serviceAdditionalRewardSum);
            totalStat.serviceAdditionalRewardSums[item.serviceAdditionalRewardSum.currency] = helper.sum(totalStat.serviceAdditionalRewardSums[item.serviceAdditionalRewardSum.currency], item.serviceAdditionalRewardSum);
          }

          if (item.serviceRewardSum && item.makerSum) {
            if (state.partners.length > 0) {
              if (!stat.partnersRewards[0]) {
                stat.partnersRewards[0] = {}
              }
              if (!stat.partnersRewards[0][item.serviceRewardSum.currency]) {
                stat.partnersRewards[0][item.serviceRewardSum.currency] = stat.serviceRewardSums[item.serviceRewardSum.currency]
              }

              if (!totalStat.partnersRewards[0]) {
                totalStat.partnersRewards[0] = {}
              }

              totalStat.partnersRewards[0][item.serviceRewardSum.currency] = helper.sum(totalStat.partnersRewards[0][item.serviceRewardSum.currency], item.serviceRewardSum)
            }

            for (const partner of state.partners) {
              if (!stat.partnersRewards[partner.userId]) {
                stat.partnersRewards[partner.userId] = {}
              }

              if (!totalStat.partnersRewards[partner.userId]) {
                totalStat.partnersRewards[partner.userId] = {}
              }

              const config = partner.config as any;
              const turnoverShare = parseFloat((item.type === Direction.PAY_IN ? config.turnoverInShare : config.turnoverOutShare) ?? '0')
              const revenueShare = parseFloat(config.revenueShare ?? '0')


              const revenue = helper.mul(stat.serviceRewardSums[item.serviceRewardSum.currency], revenueShare)
              const turnover = helper.mul(stat.makerSums[item.serviceRewardSum.currency], turnoverShare)
              stat.partnersRewards[partner.userId][item.serviceRewardSum.currency] = helper.sum(revenue, turnover)
              stat.partnersRewards[0][item.serviceRewardSum.currency] = helper.sub(stat.partnersRewards[0][item.serviceRewardSum.currency], stat.partnersRewards[partner.userId][item.serviceRewardSum.currency])

              const revenueTotal = helper.mul(totalStat.serviceRewardSums[item.serviceRewardSum.currency], revenueShare)
              const turnoverTotal = helper.mul(totalStat.makerSums[item.serviceRewardSum.currency], turnoverShare)
              totalStat.partnersRewards[partner.userId][item.serviceRewardSum.currency] = helper.sum(revenueTotal, turnoverTotal)
              totalStat.partnersRewards[0][item.serviceRewardSum.currency] = helper.sub(totalStat.partnersRewards[0][item.serviceRewardSum.currency], stat.partnersRewards[partner.userId][item.serviceRewardSum.currency])
            }

          }
        }


        state.groups = groups;

        if (state.requestDTO.groupBy === 'store') {
          const stores = (await api.merchantStores({limit: groupIds.length, offset: 0} as PaginationDTO, {ids: groupIds})).items;
          for (const store of stores) {
            if (!state.groups[store.id.toString()]) {
              continue;
            }

            state.groups[store.id.toString()].store = store;
          }
        } else {
          const traders = (await api.traders({limit: groupIds.length, offset: 0} as PaginationDTO, {ids: groupIds})).items;
          for (const trader of traders) {
            if (!state.groups[trader.id.toString()]) {
              continue;
            }

            state.groups[trader.id.toString()].trader = trader;
          }
        }
        if (store.getters.isTrader) {
          state.groups = {'0': totalGroup}
        } else {
          state.groups['0'] = totalGroup
        }

      } finally {
        loader.value?.hide();
      }
    }

    const clearFilters = async () => {
      state.tradersFilter = [];
      state.storesFilter = [];
      state.merchantsFilter = [];
      state.devicesFilter = [];
      state.trafficGroupsFilter = [];
      state.teamLeadFilter = null;
      state.requestDTO = {
        fromDate: moment().format('YYYY-MM-DD 00:00:00'),
        groupBy: defaultGroupBy,
        options: [],
        methods: [],
        requisites: null,
      }
    }

    const canExport = computed(() => {
      return store.getters.isMerchant
        || store.getters.isMerchantOperator
        || store.getters.isTrader
        || store.getters.isTraderOperator;
    })

    const downloadReport = async () => {
      if (!canExport.value) {
        return;
      }

      loader.value?.show()

      try {
        const fallbackTo = moment().format('YYYY-MM-DD HH:mm:ss');
        const fromDate = dateHelper.toUTC(state.requestDTO.fromDate);
        const toDate = dateHelper.toUTC(state.requestDTO.toDate ?? fallbackTo);

        let exportSysname = 'deals_registry';
        let exportParams: any = {
          ...state.requestDTO,
          fromDate,
          toDate,
          amountFrom: state.requestDTO.amountFrom,
          amountTo: state.requestDTO.amountTo,
        };

        if (store.getters.isTrader || store.getters.isTraderOperator) {
          exportSysname = 'trader_deals_export';
          exportParams = { fromDate, toDate };
        } else if (store.getters.isMerchant || store.getters.isMerchantOperator) {
          state.requestDTO.traderIds = state.tradersFilter.map((value) => value.id)
          state.requestDTO.storeIds = state.storesFilter.map((value) => value.id)
          state.requestDTO.merchantIds = state.merchantsFilter.map((value) => value.id)
          state.requestDTO.trafficGroupsIds = state.trafficGroupsFilter.map((value) => value.id)
          state.requestDTO.deviceIds = state.devicesFilter.map((value) => value.id)
          state.requestDTO.teamLeadId = state.teamLeadFilter?.id
          exportParams = {
            ...state.requestDTO,
            fromDate,
            toDate,
            amountFrom: state.requestDTO.amountFrom,
            amountTo: state.requestDTO.amountTo,
            reportType: 'merchant',
          };
        }

        const data = await api.export(exportSysname, exportParams);
        const link = document.createElement("a");
        const url = URL.createObjectURL(new Blob([data]));
        link.href = url
        link.setAttribute('download', 'report.xlsx');
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      } finally {
        loader.value?.hide()
      }
    }

    const getOrderByValue = (item: SummaryStat) => {
      if (state.orderBy.field === 'count') {
        return item.count;
      }

      if (state.orderBy.field === 'taker_sum') {
        const sums = Object.values(item.takerSums ?? {})
        let total = 0;
        for (const sum of sums) {
          total += parseFloat(sum.amount ?? 0)
        }
        return total;
      }

      return 0;
    }

    const sortedGroups = computed(() => {
      let data = Object.values(state.groups)

      const status = state.orderByStatus
      const direction = state.orderByType

      return data.sort((a, b) => {
        if (a.groupId === '0') {
          return 1;
        }

        if (b.groupId === '0') {
          return 1;
        }

        const aValue = getOrderByValue((a.byDirections[direction] ?? {})[status] ?? {})
        const bValue =getOrderByValue((b.byDirections[direction] ?? {})[status] ?? {})

        return state.orderBy.direction === 'asc' ? (aValue > bValue ? 1 : -1) : (aValue > bValue ? -1 : 1)
      })
    })

    const applyFilters = async () => {
      loadSummary()
    }

    onMounted(async () => {
      await loadPartners()
      loadSummary()
    });

    return {
      ...toRefs(state),
      user: computed(() => store.getters.getUser),
      isOperator: computed(() => store.getters.isOperator),
      isAdmin: computed(() => store.getters.isAdmin),
      isTrader: computed(() => store.getters.isTrader),
      isTeamLead: computed(() => store.getters.isTeamLead),
      isReferral: computed(() => store.getters.isReferral),
      isMerchant: computed(() => store.getters.isMerchant),
      isMerchantOperator: computed(() => store.getters.isMerchantOperator),
      isTraderOperator: computed(() => store.getters.isTraderOperator),
      loader,
      dateHelper,
      numberFormatter,
      helper,
      clearFilters,
      downloadReport,
      canExport,
      UserRole,
      DealStatus,
      Direction,
      applyFilters,
      rewardFields,
      mainFields,
      store,
      onTraderListUpdate,
      deviceMultiselect,
      orderByFields,
      sortedGroups,
      statuses
    }
  }
})
</script>
